import api from "../../../api";

export const METADATA_SYNC_INTERVAL_MS = 3000;
const DEVICE_FALLBACK_TTL_MS = 5 * 60 * 1000;
const LOCATION_FALLBACK_TTL_MS = 10 * 60 * 1000;
const GLOBAL_FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

let ensureRequest = null;
let pollTimer = null;
let visibilityHandler = null;

const state = {
  scopeKey: null,
  appliedRevisions: {
    devices: null,
    locations: null,
    deviceTypes: null,
  },
  lastCheckedAt: 0,
  status: "idle",
};

const mutations = {
  SET_SCOPE(state, scopeKey) {
    if (state.scopeKey === scopeKey) return;
    state.scopeKey = scopeKey;
    state.appliedRevisions = {
      devices: null,
      locations: null,
      deviceTypes: null,
    };
  },
  SET_STATUS(state, status) {
    state.status = status;
  },
  SET_LAST_CHECKED_AT(state, value) {
    state.lastCheckedAt = value;
  },
  SET_APPLIED_REVISION(state, { key, revision }) {
    state.appliedRevisions = {
      ...state.appliedRevisions,
      [key]: revision,
    };
  },
  RESET(state) {
    state.scopeKey = null;
    state.appliedRevisions = {
      devices: null,
      locations: null,
      deviceTypes: null,
    };
    state.lastCheckedAt = 0;
    state.status = "idle";
  },
};

function responseBody(response) {
  return response && response.body ? response.body : response;
}

async function loadAndApply(dispatch, commit, key, revision, action) {
  const result = await dispatch(action, { force: true, silent: true }, { root: true });
  if (result !== null) {
    commit("SET_APPLIED_REVISION", { key, revision });
  }
}

const actions = {
  async ensureMetadata({ commit, dispatch, state, rootState }, options = {}) {
    if (ensureRequest) return ensureRequest;

    ensureRequest = (async () => {
      commit("SET_STATUS", "checking");
      let revisions = null;
      try {
        revisions = responseBody(await api.getSyncRevisions());
      } catch (error) {
        if (error && error.status === 401) {
          commit("TO_LOGIN", true, { root: true });
        }
      }

      const jobs = [];
      if (revisions) {
        const now = Date.now();
        const devicesMissing = rootState.device.statusOnly !== "success" ||
          rootState.device.powerDevicesOnlyKey !== JSON.stringify({ type: null, scope: null });
        const locationsMissing = rootState.location.status !== "success";
        const globalsMissing = !rootState.global.globalAllLoadedAt || !rootState.global.globalLoadedAt;
        const devicesExpired = now - rootState.device.powerDevicesOnlyLoadedAt >= DEVICE_FALLBACK_TTL_MS;
        const locationsExpired = now - rootState.location.loadedAt >= LOCATION_FALLBACK_TTL_MS;
        const globalsExpired = now - Math.min(
          rootState.global.globalLoadedAt || 0,
          rootState.global.globalAllLoadedAt || 0
        ) >= GLOBAL_FALLBACK_TTL_MS;
        const deviceTypesChanged = state.appliedRevisions.deviceTypes !== revisions.deviceTypes;

        if (
          options.force ||
          devicesMissing ||
          devicesExpired ||
          deviceTypesChanged ||
          state.appliedRevisions.devices !== revisions.devices
        ) {
          jobs.push(loadAndApply(
            dispatch,
            commit,
            "devices",
            revisions.devices,
            "device/getPowerDevicesOnly"
          ));
        }
        if (options.force || locationsMissing || locationsExpired || state.appliedRevisions.locations !== revisions.locations) {
          jobs.push(loadAndApply(
            dispatch,
            commit,
            "locations",
            revisions.locations,
            "location/getLocation"
          ));
        }
        if (options.force || globalsMissing || globalsExpired || deviceTypesChanged) {
          jobs.push(Promise.all([
            dispatch("global/getGlobal", { force: true, silent: true }, { root: true }),
            dispatch("global/getGlobalAll", { force: true, silent: true }, { root: true }),
          ]).then((results) => {
            if (results.every((result) => result !== null)) {
              commit("SET_APPLIED_REVISION", {
                key: "deviceTypes",
                revision: revisions.deviceTypes,
              });
            }
          }));
        }
      } else {
        // TTL-based fallback when the tiny revision endpoint is temporarily unavailable.
        jobs.push(
          dispatch("device/getPowerDevicesOnly", { silent: true }, { root: true }),
          dispatch("location/getLocation", { silent: true }, { root: true }),
          dispatch("global/getGlobal", { silent: true }, { root: true }),
          dispatch("global/getGlobalAll", { silent: true }, { root: true })
        );
      }

      await Promise.allSettled(jobs);
      commit("SET_LAST_CHECKED_AT", Date.now());
      commit("SET_STATUS", revisions ? "ready" : "degraded");
      return revisions;
    })();

    try {
      return await ensureRequest;
    } finally {
      ensureRequest = null;
    }
  },

  start({ commit, dispatch, state }, { scopeKey } = {}) {
    const normalizedScope = String(scopeKey || "");
    const scopeChanged = state.scopeKey !== normalizedScope;
    commit("SET_SCOPE", normalizedScope);
    void dispatch("ensureMetadata", { force: scopeChanged });

    if (!pollTimer) {
      pollTimer = window.setInterval(() => {
        if (!document.hidden) void dispatch("ensureMetadata");
      }, METADATA_SYNC_INTERVAL_MS);
    }
    if (!visibilityHandler) {
      visibilityHandler = () => {
        if (!document.hidden) void dispatch("ensureMetadata");
      };
      document.addEventListener("visibilitychange", visibilityHandler);
    }
  },

  stop({ commit }, { reset = false } = {}) {
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
    if (reset) commit("RESET");
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
